// VESPER LUNA hero slider — vanilla custom element.
// Features: autoplay, manual nav (arrows/thumbs/dots), touch swipe, pause on hover/visibility.

class VlHeroSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-vl-hero-viewport]');
    this.track = this.querySelector('[data-vl-hero-track]');
    this.slides = [...this.querySelectorAll('.vl-hero-slider__slide')];
    this.thumbs = [...this.querySelectorAll('[data-vl-hero-thumb]')];
    this.dots = [...this.querySelectorAll('[data-vl-hero-dot]')];
    this.prevBtn = this.querySelector('[data-vl-hero-prev]');
    this.nextBtn = this.querySelector('[data-vl-hero-next]');

    this.count = this.slides.length;
    if (this.count === 0) return;

    this.index = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.duration = parseInt(this.dataset.duration || '5', 10) * 1000;
    this.loop = this.dataset.loop === 'true';
    this.timer = null;
    this.isHovered = false;
    this.isInteracting = false;

    // Wire navigation
    this.prevBtn?.addEventListener('click', () => this._userGo(-1));
    this.nextBtn?.addEventListener('click', () => this._userGo(1));
    this.thumbs.forEach(t => {
      t.addEventListener('click', () => this._userGoTo(parseInt(t.dataset.index, 10)));
    });
    this.dots.forEach(d => {
      d.addEventListener('click', () => this._userGoTo(parseInt(d.dataset.index, 10)));
    });

    // Touch / swipe
    this._setupSwipe();

    // Keyboard
    this.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') this._userGo(-1);
      else if (e.key === 'ArrowRight') this._userGo(1);
    });

    // Pause autoplay on hover (desktop) and tab visibility
    this.addEventListener('mouseenter', () => { this.isHovered = true; this._stopAutoplay(); });
    this.addEventListener('mouseleave', () => { this.isHovered = false; this._startAutoplay(); });
    this._visibilityHandler = () => {
      if (document.visibilityState === 'hidden') this._stopAutoplay();
      else this._startAutoplay();
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);

    // Resize → re-set transform to current index (in case viewport size changed)
    this._resizeHandler = () => this._update(false);
    window.addEventListener('resize', this._resizeHandler, { passive: true });

    // Honor reduced motion: disable autoplay
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.autoplay = false;
    }

    // Initial render
    this._update(false);
    this._startAutoplay();
  }

  disconnectedCallback() {
    this._stopAutoplay();
    if (this._visibilityHandler) document.removeEventListener('visibilitychange', this._visibilityHandler);
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  }

  _userGo(direction) {
    let next = this.index + direction;
    if (this.loop) {
      if (next < 0) next = this.count - 1;
      if (next >= this.count) next = 0;
    } else {
      if (next < 0 || next >= this.count) return;
    }
    this._goTo(next, true);
  }

  _userGoTo(idx) {
    if (idx < 0 || idx >= this.count || idx === this.index) return;
    this._goTo(idx, true);
  }

  _goTo(idx, animate = true) {
    this.index = idx;
    this.toggleAttribute('data-no-anim', !animate);
    this._update(animate);
    // Restart autoplay timer after manual interaction
    this._stopAutoplay();
    this._startAutoplay();
  }

  _update(animate) {
    if (!this.track) return;
    const offset = -100 * this.index;
    if (!animate) {
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${offset}%)`;
      // Force reflow then re-enable transitions for subsequent updates
      requestAnimationFrame(() => {
        this.track.style.transition = '';
        this.removeAttribute('data-no-anim');
      });
    } else {
      this.track.style.transform = `translateX(${offset}%)`;
    }

    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    this.thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === this.index);
      t.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });
    this.dots.forEach((d, i) => {
      d.classList.toggle('is-active', i === this.index);
      d.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });

    // Disable arrow buttons at edges if not looping
    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', this.index === 0);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', this.index === this.count - 1);
    }
  }

  _startAutoplay() {
    if (!this.autoplay || this.count <= 1 || this.isHovered || this.isInteracting) return;
    this._stopAutoplay();
    this.timer = setInterval(() => {
      let next = this.index + 1;
      if (next >= this.count) {
        if (this.loop) next = 0;
        else { this._stopAutoplay(); return; }
      }
      this._goToInternal(next);
    }, this.duration);
  }

  _stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Internal-only goTo that does NOT restart timer (used by autoplay tick itself)
  _goToInternal(idx) {
    this.index = idx;
    this._update(true);
  }

  _setupSwipe() {
    const minSwipe = 40;
    let startX = 0, startY = 0, dx = 0, dy = 0, dragging = false;

    const onStart = (e) => {
      dragging = true;
      this.isInteracting = true;
      this._stopAutoplay();
      const t = e.touches ? e.touches[0] : e;
      startX = t.clientX;
      startY = t.clientY;
      dx = 0; dy = 0;
    };
    const onMove = (e) => {
      if (!dragging) return;
      const t = e.touches ? e.touches[0] : e;
      dx = t.clientX - startX;
      dy = t.clientY - startY;
      // If the user is mainly scrolling vertically, don't hijack
      if (Math.abs(dy) > Math.abs(dx) + 10) return;
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      this.isInteracting = false;
      if (Math.abs(dx) > minSwipe && Math.abs(dx) > Math.abs(dy)) {
        this._userGo(dx < 0 ? 1 : -1);
      } else {
        this._startAutoplay();
      }
      dx = 0; dy = 0;
    };

    this.viewport.addEventListener('touchstart', onStart, { passive: true });
    this.viewport.addEventListener('touchmove', onMove, { passive: true });
    this.viewport.addEventListener('touchend', onEnd, { passive: true });
    this.viewport.addEventListener('touchcancel', onEnd, { passive: true });
  }
}

if (!customElements.get('vl-hero-slider')) {
  customElements.define('vl-hero-slider', VlHeroSlider);
}
